import React, { useCallback } from 'react';
import { FiX } from 'react-icons/fi';
import {
  ColumnFilter,
  FILTERABLE_COLUMNS,
  getSearchTypeOptionsForColumn,
  getFilterableColumn,
} from './filter-types';
import { DocumentFilterSearchType } from '../../schemas/boolean_filtering';

interface FilterRowProps {
  filter: ColumnFilter;
  onUpdate: (updates: Partial<Omit<ColumnFilter, 'id'>>) => void;
  onRemove: () => void;
}

export const FilterRow: React.FC<FilterRowProps> = ({ filter, onUpdate, onRemove }) => {
  const column = getFilterableColumn(filter.columnId);
  const searchTypeOptions = column ? getSearchTypeOptionsForColumn(column) : [];

  const handleColumnChange = useCallback((e: React.ChangeEvent<HTMLSelectElement>) => {
    onUpdate({ columnId: e.target.value });
  }, [onUpdate]);

  const handleSearchTypeChange = useCallback((e: React.ChangeEvent<HTMLSelectElement>) => {
    onUpdate({ searchType: e.target.value as DocumentFilterSearchType });
  }, [onUpdate]);

  const handleValueChange = useCallback((e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    onUpdate({ value: e.target.value });
  }, [onUpdate]);

  const handleKeyDown = useCallback((e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      // Could trigger search here if needed
    }
  }, []);

  // Common input styling using theme colors
  const inputClassName = "flex-1 min-w-0 rounded border border-zinc-300 px-2 py-1 text-sm text-[#1c1c24] focus:border-[#8c8ca3] focus:outline-none focus:ring-1 focus:ring-[#8c8ca3]";
  const selectClassName = "rounded border border-zinc-300 px-2 py-1 text-sm text-[#1c1c24] focus:border-[#8c8ca3] focus:outline-none focus:ring-1 focus:ring-[#8c8ca3]";

  const renderValueInput = () => {
    if (!column) {
      return (
        <input
          type="text"
          value={filter.value}
          onChange={handleValueChange}
          onKeyDown={handleKeyDown}
          placeholder="Filter value..."
          className={inputClassName}
        />
      );
    }

    if (column.type === 'enum' && column.enumValues) {
      return (
        <select
          value={filter.value}
          onChange={handleValueChange}
          className={inputClassName}
        >
          <option value="">Select...</option>
          {column.enumValues.map(val => (
            <option key={val} value={val}>{val}</option>
          ))}
        </select>
      );
    }

    if (column.type === 'date') {
      return (
        <input
          type="date"
          value={filter.value}
          onChange={handleValueChange}
          onKeyDown={handleKeyDown}
          className={inputClassName}
        />
      );
    }

    return (
      <input
        type="text"
        value={filter.value}
        onChange={handleValueChange}
        onKeyDown={handleKeyDown}
        placeholder={`Filter ${column.label.toLowerCase()}...`}
        className={inputClassName}
      />
    );
  };

  return (
    <div className="flex items-center gap-2 py-1">
      {/* Column selector */}
      <select
        value={filter.columnId}
        onChange={handleColumnChange}
        className={`w-32 ${selectClassName}`}
      >
        {FILTERABLE_COLUMNS.map(col => (
          <option key={col.id} value={col.id}>{col.label}</option>
        ))}
      </select>

      {/* Search type selector */}
      <select
        value={filter.searchType}
        onChange={handleSearchTypeChange}
        className={`w-36 ${selectClassName}`}
      >
        {searchTypeOptions.map(opt => (
          <option key={opt.value} value={opt.value}>{opt.label}</option>
        ))}
      </select>

      {/* Value input */}
      {renderValueInput()}

      {/* Remove button */}
      <button
        onClick={onRemove}
        className="p-1 text-zinc-400 hover:text-red-500 transition-colors"
        title="Remove filter"
      >
        <FiX className="w-[1em] h-[1em]" />
      </button>
    </div>
  );
};

export default FilterRow;